import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { environment as sEnv } from '@accedo/xdk-core';
import { vKey } from '@accedo/xdk-virtual-key';
import PropTypes from 'prop-types';
import cx from 'classnames';

import BaseModal from '../../common/BaseModal/BaseModal';
import { modalScreenNames } from '#/config/constants';
import {
  getAgeGroupConfig,
  getEntryById,
} from '#/redux/modules/accedoOne/selectors';
import { actions } from '#/redux/modules/modal/actions';
import { getModalData } from '#/redux/modules/modal/selectors';
import { getCustomDataDisplayedModals } from '#/redux/modules/identity/selectors';
import {
  sendWelcomeModal,
  sendWelcomeModalCTAClicked,
  sendWelcomeModalPlayIconClicked,
  sendWelcomeModalCancelClicked,
} from '#/redux/modules/firestore/actions';
import styles from './WelcomeModal.scss';
import FocusModal from '../../common/FocusModal/FocusModal';
import { WELCOME_MODAL } from '#/utils/navigationHelper';
import { isWorkstation } from '#/utils/componentStyleConfig';
import { FocusDiv } from '#/widgets/ui';
import { isGuestMode } from '#/redux/modules/user/selectors';

const {
  CONTAINER,
  CONTENT_AREA,
  SUCCESS_ACTION,
  DISMISS_ACTION,
} = WELCOME_MODAL;

const { BACK } = vKey;

const WelcomeModal = ({ history }) => {
  const dispatch = useDispatch();
  const { modalId } = useSelector(state => getModalData(state));

  const ageGroupConfig = useSelector(getAgeGroupConfig);
  const modalStatus = useSelector(getCustomDataDisplayedModals);
  const guestMode = useSelector(isGuestMode);
  const welcomeModalEntryData =
    useSelector(state => getEntryById(state, modalId)) || null;

  useEffect(() => {
    dispatch(sendWelcomeModal(modalScreenNames.WelcomeModal));
  }, []);

  const handleWatchButton = async () => {
    if (welcomeModalEntryData?.sponsorPlaylistId) {
      history.replace('/');
      history.push(
        `${welcomeModalEntryData?.ctaDeeplinkUrl}?collectionid=${welcomeModalEntryData?.sponsorPlaylistId}`,
      );
    } else {
      history.replace('/');
      history.push(`${welcomeModalEntryData?.ctaDeeplinkUrl}`);
    }

    dispatch(
      sendWelcomeModalCTAClicked(
        modalScreenNames.WelcomeModal,
        welcomeModalEntryData?.title,
      ),
    );

    dispatch(actions.closeModal());
  };

  const handlePlayIcon = async () => {
    history.replace(`${welcomeModalEntryData?.ctaDeeplinkUrl}`);

    dispatch(
      sendWelcomeModalPlayIconClicked(
        modalScreenNames.WelcomeModal,
        welcomeModalEntryData?.title,
      ),
    );

    dispatch(actions.closeModal());
  };

  const handleClose = async () => {
    dispatch(
      sendWelcomeModalCancelClicked(
        modalScreenNames.WelcomeModal,
        welcomeModalEntryData?.title,
      ),
    );

    if (!guestMode) {
      window?.location.reload();
    } else {
      dispatch(actions.closeModal());
    }
  };

  const handleCancel = async () => {
    dispatch(
      sendWelcomeModalCancelClicked(
        modalScreenNames.WelcomeModal,
        welcomeModalEntryData?.title,
      ),
    );
    dispatch(actions.closeModal());
  };

  // TV back button handler
  useEffect(() => {
    const onBackNav = e => {
      const { id } = e;

      if (id === BACK.id) {
        // e.preventDefault();
        // e.stopPropagation();

        dispatch(
          sendWelcomeModalCancelClicked(
            modalScreenNames.WelcomeModal,
            welcomeModalEntryData?.title,
          ),
        );
        // dispatch(actions.closeModal());

        return false;
      }
    };

    sEnv.addEventListener(sEnv.SYSTEM.KEYDOWN, onBackNav);

    return () => {
      sEnv.removeEventListener(sEnv.SYSTEM.KEYDOWN, onBackNav);
    };
  }, []);

  const modal = useMemo(() => {
    return (
      <FocusModal autofocus avoidBackHandler>
        <BaseModal
          nav={{
            parent: CONTAINER,
            id: SUCCESS_ACTION,
            nextup: welcomeModalEntryData?.displayPlayButton && CONTENT_AREA,
          }}
          navCancel={{
            parent: CONTAINER,
            id: DISMISS_ACTION,
            nextup: welcomeModalEntryData?.displayPlayButton && CONTENT_AREA,
          }}
          className={styles.baseContainer}
          saveButtonContainerClassName={styles.watchButtonContainer}
          saveButtonClassName={styles.watchButton}
          closeIconClassName={styles.closex}
          saveButtonText={
            welcomeModalEntryData?.displayActionButton
              ? welcomeModalEntryData?.buttonActionText
              : welcomeModalEntryData?.buttonText
          }
          saveAction={handleWatchButton}
          closeAction={handleClose}
          cancelAction={handleCancel}
          showSaveButton={!welcomeModalEntryData?.displayDismissButton}
          showCancelButton={welcomeModalEntryData?.displayDismissButton}
          cancelButtonText={welcomeModalEntryData?.buttonDismissText}
          showCloseX={!!isWorkstation}
        >
          <FocusDiv
            className={styles.modalContainer}
            nav={{
              id: CONTAINER,
              forwardFocus: welcomeModalEntryData?.displayDismissButton
                ? DISMISS_ACTION
                : SUCCESS_ACTION,
            }}
          >
            {(welcomeModalEntryData?.sponsorText ||
              welcomeModalEntryData?.sponsorLogoUrl) && (
              <div
                className={cx(
                  styles.titleContainer,
                  !welcomeModalEntryData?.sponsorText &&
                    styles.titleContainerImageOnly,
                )}
              >
                {welcomeModalEntryData?.sponsorText && (
                  <>
                    <span>{`${welcomeModalEntryData?.sponsorText}`}</span>{' '}
                    <span>&nbsp;</span>
                  </>
                )}
                {welcomeModalEntryData?.sponsorLogoUrl && (
                  <img
                    src={welcomeModalEntryData?.sponsorLogoUrl}
                    alt="sponsor logo"
                  />
                )}
              </div>
            )}
            <FocusDiv
              onClick={handlePlayIcon}
              className={styles.modalBodyContainer}
              classNameFocused={!isWorkstation() && styles.tvFocused}
              nav={{
                parent: CONTAINER,
                id: welcomeModalEntryData?.displayPlayButton && CONTENT_AREA,
                nextdown: welcomeModalEntryData?.displayDismissButton
                  ? DISMISS_ACTION
                  : SUCCESS_ACTION,
              }}
            >
              <img
                className={styles.modalImage}
                src={welcomeModalEntryData?.imageUrl}
                alt="modal url"
              />
              {welcomeModalEntryData?.displayPlayButton && (
                <div className={styles.modalImageOverlayContainer}>
                  <img
                    className={styles.modalImageOverlay}
                    src={welcomeModalEntryData?.playIconImage.fileUrl}
                    alt="Play"
                  />
                </div>
              )}
              {welcomeModalEntryData?.imageLabel && (
                <div className={styles.overlay}>
                  {welcomeModalEntryData?.imageLabel}
                </div>
              )}
            </FocusDiv>
            <h3 className={styles.displayText}>
              {welcomeModalEntryData?.displayText}
            </h3>
          </FocusDiv>
        </BaseModal>
      </FocusModal>
    );
  }, [ageGroupConfig, modalStatus, welcomeModalEntryData]);

  return <>{modal}</>;
};

WelcomeModal.propTypes = {
  history: PropTypes.object,
};

export default withRouter(WelcomeModal);
